/**
 * Per-draft AI-analysis prompt override (Jinja2).
 *
 * This still lives on `ai_config.prompt_override` (a single string).
 * In a future iteration this will move to `prompt_templates_v2`
 * with `scope=report` — for now we keep the simpler in-config field.
 *
 * Iter 27 — header + info banner + "Clear override" button so the
 * admin understands what this field is for. Before iter 27 the
 * sub-tab just showed a giant textarea with no context; the user
 * flagged "no sé para qué necesito esto" during the mockup review.
 */

import { Info, Trash2 } from "lucide-react";

import { useT } from "@/i18n";
import { Disclosure } from "@pervasivemind/admin-ui";
import type { UseBuilderDraft } from "@/hooks/useBuilderDraft";

interface PromptOverrideEditorProps {
  draft: UseBuilderDraft;
}

export function PromptOverrideEditor({
  draft,
}: PromptOverrideEditorProps): JSX.Element {
  const { t } = useT();
  const ai = draft.ai;
  const hasOverride = Boolean(ai.prompt_override && ai.prompt_override.trim());

  return (
    <div className="space-y-3">
      <header className="space-y-2">
        <h3 className="text-base font-semibold text-neutral-900">
          {t("editor.ai.promptLabel")}
        </h3>
        <p className="flex items-start gap-2 rounded border-l-2 border-info/50 bg-info/10 px-3 py-2 text-xs leading-relaxed text-info-dark">
          <Info size={14} className="mt-0.5 shrink-0 text-info-dark" />
          <span>{t("editor.ai.promptOverrideInfo")}</span>
        </p>
      </header>

      <label className="block">
        <span className="mb-1 block text-xs font-semibold text-neutral-600">
          {t("editor.ai.promptTemplateLabel")}
        </span>
        <textarea
          rows={28}
          value={ai.prompt_override ?? ""}
          onChange={(e) =>
            draft.setAI({ prompt_override: e.target.value || null })
          }
          placeholder={t("editor.ai.promptPlaceholder")}
          className="block w-full rounded-[10px] border border-control bg-white px-3 py-2 placeholder:text-neutral-500 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary min-h-[600px] resize-y font-mono text-xs leading-relaxed"
        />
      </label>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => draft.setAI({ prompt_override: null })}
          disabled={!hasOverride}
          className="inline-flex items-center gap-1 rounded-md border border-neutral-300 px-3 py-1.5 text-xs font-medium text-neutral-600 transition-colors hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-50"
          title={
            hasOverride
              ? t("editor.ai.promptClearOverride")
              : t("editor.ai.promptNoOverride")
          }
        >
          <Trash2 size={12} />
          {t("editor.ai.promptClearOverride")}
        </button>
        {!hasOverride && (
          <span className="text-xs italic text-neutral-500">
            {t("editor.ai.promptNoOverride")}
          </span>
        )}
      </div>

      <Disclosure summary={t("editor.ai.promptVarsLabel")}>
        <p className="mt-2 leading-relaxed">
          {t("editor.ai.promptVarsHint")}
        </p>
        <code className="mt-1 block font-mono text-xs leading-relaxed text-neutral-700">
          {`{{ report.name }}, {{ kpis }}, {{ top_rows }}, {{ charts }}, {{ today_iso }}, {{ focus_areas }}`}
        </code>
      </Disclosure>
    </div>
  );
}
